/**
 * WebRTC Host Service
 *
 * The DESKTOP side of "access your machines from anywhere": it receives WebRTC
 * commands from a remote web client and executes them on this machine, so the
 * web client can browse this machine's projects/files and drive its terminals.
 *
 * Historically this proxied each command to Tauri `invoke()`. The migrated app
 * has no Tauri runtime — instead the desktop's own backend (CefBackend) round-
 * trips to the headless core. So the `invoke`/`listen` shims below now route
 * every incoming remote command through the SAME local backend the desktop UI
 * uses (mirrors the old Tauri command 1:1), and `isWebRTCHostAvailable()` is
 * true under the CEF host. The command-handler table is unchanged; only the
 * dispatch target moved from Tauri to CefBackend.
 */

import type { Project, CreateProjectInput, UpdateProjectInput, ProjectAnalysisResult, AgentInfo } from '../types/tauri';
import type { IBackend } from './backend/IBackend';
import { peerConnection } from './peerConnection';
import { logger } from '@/lib/logger';

type UnlistenFn = () => void;

// The local backend (CefBackend on the desktop) the host executes remote
// commands against. Lazy import avoids a circular dependency
// (backend → WebRTCBackend → peerConnection → here).
let _getBackend: (() => IBackend) | null = null;
async function localBackend(): Promise<IBackend> {
  if (!_getBackend) _getBackend = (await import('./backend')).getBackend;
  return _getBackend();
}

// Execute a (legacy Tauri-named) command against the local backend. Keeps the
// command-handler table below unchanged — only the dispatch target moves from
// Tauri invoke() to the CefBackend method that mirrors it 1:1.
async function invoke<T>(command: string, args: Record<string, unknown> = {}): Promise<T> {
  const b = await localBackend();
  const p = args || {};
  const run = async (): Promise<unknown> => {
    switch (command) {
      // Projects
      case 'get_projects': return b.projects.list();
      case 'get_project': return b.projects.get(p.id as string);
      case 'create_project': return b.projects.create(p.input as CreateProjectInput);
      case 'update_project': return b.projects.update(p.id as string, p.updates as UpdateProjectInput);
      case 'delete_project': return b.projects.delete(p.id as string);
      case 'has_recent_activity': return b.projects.hasRecentActivity(p.projectId as string);
      case 'analyze_project_directory': return b.projects.analyze(p.path as string);
      case 'analyze_project_with_ai': return b.projects.analyzeWithAI(p.path as string);
      case 'generate_project_details': return b.projects.generateDetails(p.projectPath as string);
      case 'get_project_stats': return b.projects.getStats(p.projectId as string);
      // Files
      case 'select_folder': return b.files.pickFolder();
      case 'read_file_content': return b.files.readFile(p.projectId as string, p.filePath as string);
      case 'get_folder_children':
        return b.files.getFolderChildren(p.projectId as string, p.folderPath as string, (p.parentGitignored as boolean) ?? false);
      case 'read_project_files': return b.files.readProjectFiles(p.projectId as string);
      case 'get_git_status': return b.files.getGitStatus(p.projectId as string);
      case 'write_file_content': return b.files.writeFile(p.projectId as string, p.filePath as string, p.content as string);
      case 'read_file_base64': return b.files.readFileBase64(p.projectId as string, p.filePath as string);
      case 'save_attachment': return b.files.saveAttachment(p.projectId as string, p.fileName as string, p.data as string);
      // Files — tree/index/cache/runners/config. These were unmapped, so the
      // remote client got "Unknown command" for file indexing, the symbol
      // index, the Run button's runners.json, and the project cache.
      case 'crawl_project_tree': return b.files.crawlProjectTree(p.projectId as string);
      case 'index_project_files': return b.files.indexProjectFiles(p.projectId as string);
      case 'read_ateliercode_runners': return b.files.readAteliercodeRunners(p.projectId as string);
      case 'read_ateliercode_config': return b.files.readAteliercodeConfig(p.projectId as string);
      case 'cache_read': return b.files.cacheRead(p.projectId as string, p.key as string);
      case 'cache_write': return b.files.cacheWrite(p.projectId as string, p.key as string, p.content as string);
      case 'cache_clear_all': return b.files.cacheClearAll();
      case 'path_exists_in_project': return b.files.pathExistsInProject(p.projectId as string, p.relativePath as string);
      case 'reveal_in_explorer': return b.files.revealInExplorer(p.path as string);
      case 'open_external': return b.files.openExternal(p.url as string);
      case 'rename_file': return b.files.renameFile(p.projectId as string, p.oldPath as string, p.newName as string);
      case 'delete_file': return b.files.deleteFile(p.projectId as string, p.filePath as string);
      case 'create_file': return b.files.createFile(p.projectId as string, p.parentPath as string, p.fileName as string);
      case 'create_folder': return b.files.createFolder(p.projectId as string, p.parentPath as string, p.folderName as string);
      case 'copy_external_into_project':
        return b.files.copyExternalIntoProject(p.projectId as string, p.sourcePath as string, p.destFolderRelative as string, p.conflictResolution as 'overwrite' | 'keepboth');
      case 'move_within_project':
        return b.files.moveWithinProject(p.projectId as string, p.sourceRelative as string, p.destFolderRelative as string);
      // System
      case 'detect_agents': return b.system.detectAgents();
      case 'get_hostname': return b.system.getHostname();
      case 'get_platform': return b.system.getPlatform();
      // Transcription
      case 'transcribe_openai': return b.transcription.transcribeOpenAI(p.audioData as string, p.apiKey as string);
      // Terminal (PTY)
      case 'persist_terminals': return b.terminal.persistAll();
      case 'list_terminals': return b.terminal.list();
      case 'create_terminal': return b.terminal.create(p.cols as number, p.rows as number, (p.cwd as string) ?? undefined);
      case 'write_terminal': return b.terminal.write(p.sessionId as number, p.data as string);
      case 'resize_terminal': return b.terminal.resize(p.sessionId as number, p.cols as number, p.rows as number);
      case 'close_terminal': return b.terminal.close(p.sessionId as number);
      // Project groups ("Teams"). Forwarded so a remote client can read AND
      // manage the host's group definitions (list renders the grouped project
      // view; upsert/delete/reorder back the Settings → Teams panel).
      case 'artifacts_list': return b.artifacts.list(p.projectId as string);
      case 'group_list': return b.groups.list();
      case 'group_upsert': return b.groups.upsert(p.group as { id: string; name: string; color?: string | null; sortOrder?: number });
      case 'group_delete': return b.groups.remove(p.id as string);
      case 'group_reorder': return b.groups.reorder(p.ids as string[]);
      default:
        throw new Error(`WebRTC host: unsupported remote command '${command}'`);
    }
  };
  return (await run()) as T;
}

// Forward the host's local terminal stream to the connected web client. Replaces
// the Tauri terminal-data/terminal-exit events with the CefBackend terminal
// subscription (which streams from the core).
async function listen<T>(
  event: string,
  handler: (event: { payload: T }) => void,
): Promise<UnlistenFn> {
  const b = await localBackend();
  if (event === 'terminal-data') {
    return b.terminal.onData((id, data) => handler({ payload: { id, data } as T }));
  }
  if (event === 'terminal-exit') {
    return b.terminal.onExit((id) => handler({ payload: { id } as T }));
  }
  return () => {};
}

interface PeerMessage {
  type: 'request' | 'response' | 'event';
  id?: string;
  command?: string;
  params?: Record<string, unknown>;
  success?: boolean;
  data?: unknown;
  error?: string;
}

type CommandHandler = (params: Record<string, unknown>) => Promise<unknown>;

/**
 * Command handlers that map WebRTC commands to Tauri invocations
 */
const commandHandlers: Record<string, CommandHandler> = {
  // Project commands
  get_projects: async () => {
    return await invoke<Project[]>('get_projects');
  },

  get_project: async (params) => {
    return await invoke<Project | null>('get_project', { id: params.id as string });
  },

  create_project: async (params) => {
    const input = params.input as CreateProjectInput;
    return await invoke<Project>('create_project', { input });
  },

  update_project: async (params) => {
    return await invoke<Project>('update_project', {
      id: params.id as string,
      updates: params.updates as UpdateProjectInput,
    });
  },

  delete_project: async (params) => {
    return await invoke<boolean>('delete_project', { id: params.id as string });
  },

  has_recent_activity: async (params) => {
    return await invoke<boolean>('has_recent_activity', { projectId: params.id as string });
  },

  analyze_project_directory: async (params) => {
    return await invoke<ProjectAnalysisResult>('analyze_project_directory', { path: params.path as string });
  },

  analyze_project_with_ai: async (params) => {
    return await invoke<ProjectAnalysisResult>('analyze_project_with_ai', { path: params.path as string });
  },

  generate_project_details: async (params) => {
    return await invoke<{ name: string; description: string }>('generate_project_details', {
      projectPath: params.projectPath as string,
    });
  },

  // File commands
  select_folder: async () => {
    return await invoke<string | null>('select_folder');
  },

  read_file_content: async (params) => {
    return await invoke<string>('read_file_content', {
      projectId: params.projectId as string,
      filePath: params.filePath as string,
    });
  },

  get_folder_children: async (params) => {
    return await invoke<unknown[]>('get_folder_children', {
      projectId: params.projectId as string,
      folderPath: params.folderPath as string,
      parentGitignored: (params.parentGitignored as boolean) ?? false,
    });
  },

  read_project_files: async (params) => {
    return await invoke<unknown[]>('read_project_files', { projectId: params.projectId as string });
  },

  get_git_status: async (params) => {
    return await invoke<unknown[]>('get_git_status', { projectId: params.projectId as string });
  },

  write_file_content: async (params) => {
    return await invoke<void>('write_file_content', {
      projectId: params.projectId as string,
      filePath: params.filePath as string,
      content: params.content as string,
    });
  },

  read_file_base64: async (params) => {
    return await invoke<string>('read_file_base64', {
      projectId: params.projectId as string,
      filePath: params.filePath as string,
    });
  },

  save_attachment: async (params) => {
    return await invoke<string>('save_attachment', {
      projectId: params.projectId as string,
      fileName: params.fileName as string,
      data: params.data as string,
    });
  },

  // Stats commands
  get_project_stats: async (params) => {
    return await invoke<unknown>('get_project_stats', { projectId: params.projectId as string });
  },

  // System commands
  detect_agents: async () => {
    return await invoke<AgentInfo[]>('detect_agents');
  },

  get_hostname: async () => {
    return await invoke<string>('get_hostname');
  },

  get_platform: async () => {
    return await invoke<string>('get_platform');
  },

  // Transcription commands
  transcribe_openai: async (params) => {
    return await invoke<{ text: string }>('transcribe_openai', {
      audioData: params.audioData as string,
      apiKey: params.apiKey as string,
    });
  },

  // RDP commands
  rdp_start: async (params) => {
    const { useSettingsStore } = await import('../stores/settingsStore');
    const { useRdpStore } = await import('../stores/rdpStore');
    const rdpSettings = useSettingsStore.getState().rdp;

    if (!rdpSettings.hostEnabled) {
      throw new Error('Remote Desktop hosting is not enabled on this machine');
    }

    if (!rdpSettings.autoAccept) {
      const requesterName = (params.requesterName as string) || 'Unknown client';
      const consentResult = await useRdpStore.getState().requestConsent(requesterName);

      if (consentResult === 'deny') {
        throw new Error('Remote Desktop request was denied by the host');
      }

      if (consentResult === 'allow_view_only') {
        useRdpStore.getState().setInputForwarding(false);
      }
    } else if (rdpSettings.defaultViewOnly) {
      useRdpStore.getState().setInputForwarding(false);
    }

    const { screenCapture } = await import('./screenCapture');

    const stream: MediaStream = await screenCapture.startNativeCapture(0);
    const track = screenCapture.getVideoTrack();
    if (!track) throw new Error('No video track after capture');

    peerConnection.addVideoTrackToHost(track, stream);
    peerConnection.configureVideoEncoding();
    peerConnection.createRdpInputChannels();
    const { setupRdpInputHandler: setupInput } = await import('./webrtcHost');
    setupInput();

    useRdpStore.getState().setSharing(true);

    if (rdpSettings.clipboardSync) {
      lastClipboardContent = '';
      clipboardPollTimer = setInterval(async () => {
        try {
          const content = await invoke<string>('rdp_clipboard_get');
          if (content && content !== lastClipboardContent) {
            lastClipboardContent = content;
            peerConnection.sendEvent('rdp_clipboard_changed', { content });
          }
        } catch {
          // Clipboard access may fail intermittently
        }
      }, 500);
    }

    screenCapture.onStop(() => {
      if (clipboardPollTimer) {
        clearInterval(clipboardPollTimer);
        clipboardPollTimer = null;
      }
      peerConnection.removeVideoTrackFromHost();
      peerConnection.sendEvent('rdp_stopped', {});
      useRdpStore.getState().setSharing(false);
      useRdpStore.getState().reset();
    });

    const dims = screenCapture.getScreenDimensions();
    const monitors = await invoke('rdp_list_monitors');

    logger.info('WebRTCHost', 'RDP started:', dims);
    return {
      screenWidth: dims?.width || 0,
      screenHeight: dims?.height || 0,
      scaleFactor: dims?.scaleFactor || 1,
      monitors,
      inputEnabled: useRdpStore.getState().inputForwardingEnabled,
    };
  },

  rdp_stop: async () => {
    const { screenCapture } = await import('./screenCapture');
    const { useRdpStore } = await import('../stores/rdpStore');
    const { teardownRdpInputHandler: teardownInput } = await import('./webrtcHost');
    teardownInput();
    if (clipboardPollTimer) {
      clearInterval(clipboardPollTimer);
      clipboardPollTimer = null;
    }
    peerConnection.removeVideoTrackFromHost();
    screenCapture.stopCapture();
    useRdpStore.getState().setSharing(false);
    useRdpStore.getState().reset();
    logger.info('WebRTCHost', 'RDP stopped');
    return { ok: true };
  },

  rdp_list_monitors: async () => {
    return await invoke('rdp_list_monitors');
  },

  rdp_mouse_move: async (params) => {
    const { useRdpStore } = await import('../stores/rdpStore');
    if (!useRdpStore.getState().inputForwardingEnabled) return;
    return await invoke('rdp_mouse_move', { x: params.x as number, y: params.y as number });
  },

  rdp_mouse_button: async (params) => {
    const { useRdpStore } = await import('../stores/rdpStore');
    if (!useRdpStore.getState().inputForwardingEnabled) return;
    return await invoke('rdp_mouse_button', {
      x: params.x as number,
      y: params.y as number,
      button: params.button as string,
      action: params.action as string,
    });
  },

  rdp_mouse_scroll: async (params) => {
    const { useRdpStore } = await import('../stores/rdpStore');
    if (!useRdpStore.getState().inputForwardingEnabled) return;
    return await invoke('rdp_mouse_scroll', {
      deltaX: params.delta_x as number,
      deltaY: params.delta_y as number,
    });
  },

  rdp_key_event: async (params) => {
    const { useRdpStore } = await import('../stores/rdpStore');
    if (!useRdpStore.getState().inputForwardingEnabled) return;
    return await invoke('rdp_key_event', {
      key: params.key as string,
      code: params.code as string,
      action: params.action as string,
      modifiers: params.modifiers as { ctrl: boolean; alt: boolean; shift: boolean; meta: boolean },
    });
  },

  rdp_clipboard_get: async () => {
    return await invoke<string>('rdp_clipboard_get');
  },

  rdp_clipboard_set: async (params) => {
    return await invoke('rdp_clipboard_set', { content: params.content as string });
  },

  rdp_switch_monitor: async (params) => {
    const monitorId = (params.monitorId as number) ?? 0;
    const { screenCapture } = await import('./screenCapture');
    peerConnection.removeVideoTrackFromHost();
    screenCapture.stopCapture();

    const stream: MediaStream = await screenCapture.startNativeCapture(monitorId);

    const track = screenCapture.getVideoTrack();
    if (!track) throw new Error('No video track after recapture');

    peerConnection.addVideoTrackToHost(track, stream);
    const dims = screenCapture.getScreenDimensions();

    return {
      screenWidth: dims?.width || 0,
      screenHeight: dims?.height || 0,
      scaleFactor: dims?.scaleFactor || 1,
    };
  },

  // Terminal persist (for app update flow)
  terminal_persist_all: async () => {
    return await invoke<void>('persist_terminals');
  },

  // Terminal (PTY) commands
  terminal_list: async () => {
    await setupTerminalEventForwarding();
    return await invoke<number[]>('list_terminals');
  },

  terminal_list_sessions: async () => {
    await setupTerminalEventForwarding();
    const { useTerminalStore } = await import('../stores/terminalStore');
    const sessions = Array.from(useTerminalStore.getState().sessions.values());
    return sessions;
  },

  terminal_create: async (params) => {
    await setupTerminalEventForwarding();
    const id = await invoke<number>('create_terminal', {
      cols: params.cols as number,
      rows: params.rows as number,
      cwd: (params.cwd as string | null) || null,
    });
    // Sync to host's local terminalStore so the tab appears on the desktop UI
    const { useTerminalStore } = await import('../stores/terminalStore');
    const title = (params.title as string) || `Terminal ${id}`;
    const projectPath = (params.cwd as string) || '__default__';
    useTerminalStore.getState().addSession(id, title, projectPath);
    return id;
  },

  terminal_write: async (params) => {
    return await invoke<void>('write_terminal', {
      sessionId: params.sessionId as number,
      data: params.data as string,
    });
  },

  terminal_resize: async (params) => {
    return await invoke<void>('resize_terminal', {
      sessionId: params.sessionId as number,
      cols: params.cols as number,
      rows: params.rows as number,
    });
  },

  terminal_close: async (params) => {
    const sessionId = params.sessionId as number;
    await invoke<void>('close_terminal', { sessionId });
    // Sync to host's local terminalStore so the tab disappears from the desktop UI
    const { useTerminalStore } = await import('../stores/terminalStore');
    useTerminalStore.getState().removeSession(sessionId);
  },

  // Detached-session lifecycle the remote client drives (attach for buffered
  // content, detach/resume to pause/restore streaming, getBuffer to restore
  // scrollback, markAgent for activity tracking). These map straight to the
  // local backend; without them the web client's terminal mount fails.
  terminal_detach: async (params) => {
    const b = await localBackend();
    await setupTerminalEventForwarding();
    return b.terminal.detach(params.sessionId as number);
  },
  terminal_attach: async (params) => {
    const b = await localBackend();
    await setupTerminalEventForwarding();
    return b.terminal.attach(params.sessionId as number);
  },
  terminal_resume: async (params) => {
    const b = await localBackend();
    return b.terminal.resume(params.sessionId as number);
  },
  terminal_get_buffer: async (params) => {
    const b = await localBackend();
    return b.terminal.getBuffer(params.sessionId as number);
  },
  terminal_mark_agent: async (params) => {
    const b = await localBackend();
    return b.terminal.markAgentSession(
      params.sessionId as number,
      params.isAgent as boolean,
      params.completionPattern as string | undefined,
    );
  },

  // Get whisper settings from host machine
  get_whisper_settings: async () => {
    const { useSettingsStore } = await import('../stores/settingsStore');
    const whisper = useSettingsStore.getState().whisper;
    // Send the FULL WhisperConfig. Omitting voiceEngineToken/Url/Model left the
    // remote client's config incomplete, so isConfigured() read .length on an
    // undefined token and crashed the terminal panel render (IDEA-006).
    return {
      provider: whisper.provider,
      openaiApiKey: whisper.openaiApiKey,
      transcriptionMode: whisper.transcriptionMode,
      voiceEngineUrl: whisper.voiceEngineUrl,
      voiceEngineToken: whisper.voiceEngineToken,
      voiceEngineModel: whisper.voiceEngineModel,
    };
  },
};

/**
 * Handle an incoming WebRTC command and return a response
 */
export async function handleWebRTCCommand(message: PeerMessage): Promise<PeerMessage> {
  if (message.type !== 'request' || !message.command) {
    return {
      type: 'response',
      id: message.id,
      success: false,
      error: 'Invalid request format',
    };
  }

  // Fall back to invoke() for any command without an explicit handler — invoke()
  // owns the full command→backend mapping, so a new command only needs a case
  // there, not a parallel commandHandlers entry. invoke() throws for genuinely
  // unknown commands, surfaced as the error response below.
  const cmd = message.command;
  const handler: CommandHandler =
    commandHandlers[cmd] ?? ((params) => invoke(cmd, params));

  try {
    logger.debug('WebRTCHost', `Executing command: ${message.command}`, message.params);
    const result = await handler(message.params || {});
    logger.debug('WebRTCHost', `Command ${message.command} succeeded`);
    return {
      type: 'response',
      id: message.id,
      success: true,
      data: result,
    };
  } catch (error) {
    logger.error('WebRTCHost', `Command ${message.command} failed:`, error);
    return {
      type: 'response',
      id: message.id,
      success: false,
      error: error instanceof Error ? error.message : 'Command execution failed',
    };
  }
}

/**
 * Set up the rdp-input channel handler on the host side.
 */
let rdpInputUnsubscribe: (() => void) | null = null;
let clipboardPollTimer: ReturnType<typeof setInterval> | null = null;
let lastClipboardContent = '';

export function setupRdpInputHandler(): void {
  if (rdpInputUnsubscribe) rdpInputUnsubscribe();

  rdpInputUnsubscribe = peerConnection.onRdpInput(async (msg) => {
    const command = msg.type as string;
    if (!command) return;

    const handler = commandHandlers[command];
    if (handler) {
      try {
        await handler(msg as Record<string, unknown>);
      } catch (err) {
        logger.warn('WebRTCHost', `rdp-input command ${command} failed:`, err);
      }
    }
  });
}

export function teardownRdpInputHandler(): void {
  if (rdpInputUnsubscribe) {
    rdpInputUnsubscribe();
    rdpInputUnsubscribe = null;
  }
}

// --- Terminal event forwarding ---

let terminalDataUnlisten: UnlistenFn | null = null;
let terminalExitUnlisten: UnlistenFn | null = null;

export async function setupTerminalEventForwarding(): Promise<void> {
  if (terminalDataUnlisten) return;

  terminalDataUnlisten = await listen<{ id: number; data: string }>('terminal-data', (event) => {
    peerConnection.sendEvent('terminal:data', event.payload);
  });

  terminalExitUnlisten = await listen<{ id: number }>('terminal-exit', (event) => {
    peerConnection.sendEvent('terminal:exit', event.payload);
  });

  logger.debug('WebRTCHost', 'Terminal event forwarding started');
}

export function teardownTerminalEventForwarding(): void {
  if (terminalDataUnlisten) {
    terminalDataUnlisten();
    terminalDataUnlisten = null;
  }
  if (terminalExitUnlisten) {
    terminalExitUnlisten();
    terminalExitUnlisten = null;
  }
  logger.debug('WebRTCHost', 'Terminal event forwarding stopped');
}

/**
 * Check if the WebRTC host is available (running in Tauri)
 */
export function isWebRTCHostAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  // CEF host: the in-app backend (CefBackend) can serve remote commands.
  if (typeof (window as { cefQuery?: unknown }).cefQuery === 'function') return true;
  // Legacy Tauri host.
  return '__TAURI_INTERNALS__' in window || '__TAURI__' in window;
}
